import type { ReportDtoT } from '@mathflat/domain/@entities/(Content)/Report'
import parse from 'date-fns/parse'
import qs from 'qs'

import reportApi from '@/api/report.api'
import type { Student } from '@/dto'
import { CommonError, Report } from '@/dto'
import { SimpleReport } from '@/dto/Report/SimpleReport'
import { CheckableColl } from '@/models/CheckableColl'
import CheckableMap from '@/models/CheckableMap'
import { setHeaderType } from '@/utils/download'

import { ReportModelV2 } from '../page/Lesson/Report/_model/report.model'
import type Controller from './Controller'

export const NOT_AVAILABLE_ERROR = 'NOT_AVAILABLE'

class ReportController implements Controller {
  adapter = {
    reportV2: {
      from(data: ReportDtoT.GetReportRealTimeV2Response | ReportDtoT.GetReportCustomV2Response) {
        return new ReportModelV2(data)
      },
    },
    simpleReport: {
      from(data: res.SimpleReport): SimpleReport {
        return new SimpleReport(data)
      },
      to({ targetDate }) {
        return qs.stringify(
          {
            targetDate,
          },
          { arrayFormat: 'comma' },
        )
      },
    },
  }

  async fetchV2ReportRealTime(params: ReportDtoT.GetReportRealTimeV2Request) {
    const { data } = await reportApi.all.getV2ReportRealTime(params)
    return data.map(this.adapter.reportV2.from)
  }

  async fetchV2ReportRealTimeMetric(params: ReportDtoT.GetReportRealTimeMetricV2Request) {
    const { data } = await reportApi.all.getV2ReportRealTimeMetric(params)
    return data
  }

  async fetchV2ReportRealTimeSetting(params: ReportDtoT.GetReportRealTimeSettingV2Request) {
    const { data } = await reportApi.all.getV2ReportRealTimeSetting(params)
    return data
  }

  async updateV2ReportRealTimeSetting(body: ReportDtoT.PutReportRealTimeSettingV2Request) {
    await reportApi.all.putV2ReportRealTimeSetting(body)
  }

  async fetchV2ReportCustom(params: ReportDtoT.GetReportCustomV2Request) {
    const { data } = await reportApi.all.getV2ReportCustom(params)
    return {
      content: data.content.map(this.adapter.reportV2.from),
      isLast: data.last ?? false,
      totalPages: data.totalPages ?? 0,
      totalElements: data.totalElements ?? 0,
    }
  }

  async fetchV2ReportCustomScoringHistoryCount(
    params: ReportDtoT.GetReportCustomScoringHistoryCountV2Request,
  ) {
    const { data } = await reportApi.all.getV2ReportCustomScoringHistoryCount(params)
    return data
  }

  async fetchV2ReportCustomScoringHistory(
    params: ReportDtoT.GetReportCustomScoringHistoryV2Request,
  ) {
    const { data } = await reportApi.all.getV2ReportCustomScoringHistory(params)
    return data
  }

  async fetchV2ReportCustomScoring(
    body: ReportDtoT.PostReportCustomScoringV2Request,
    signal?: AbortSignal,
  ) {
    const { data } = await reportApi.all.postV2ReportCustomScoring(body, signal)
    return data
  }

  async fetchV2ReportCustomReportIdScoring(
    params: ReportDtoT.GetReportCustomReportIdScoringV2Request,
  ) {
    const { data } = await reportApi.all.getV2ReportCustomReportIdScoring(params)
    return data
  }

  async createV2ReportCustom(body: ReportDtoT.PostReportCustomV2Request) {
    const { data } = await reportApi.all.postV2ReportCustom(body)
    return data
  }

  async updateV2ReportCustom(body: ReportDtoT.PutReportCustomV2Request) {
    await reportApi.all.putV2ReportCustom(body)
  }

  async shareV2ReportCustomToParentApp(body: ReportDtoT.PostReportCustomShareParentAppV2Request) {
    await reportApi.all.postV2ReportCustomShareParentApp(body)
  }

  async unshareV2ReportCustomToParentApp(
    body: ReportDtoT.PostReportCustomUnshareParentAppV2Request,
  ) {
    await reportApi.all.postV2ReportCustomUnshareParentApp(body)
  }

  async shareV2ReportCustomToParentSms(body: ReportDtoT.PostReportCustomShareParentSmsV2Request) {
    await reportApi.all.postV2ReportCustomShareParentSms(body)
  }

  async shareReportRealTimeToParentApp(body: ReportDtoT.PostReportRealTimeShareParentAppRequest) {
    await reportApi.all.postReportRealTimeShareParentApp(body)
  }

  async shareReportRealTimeToParentSms(body: ReportDtoT.PostReportRealTimeShareParentSmsRequest) {
    await reportApi.all.postReportRealTimeShareParentSms(body)
  }

  async shareV2ReportCustomByLink(body: ReportDtoT.PostReportCustomShareByLinkV2Request) {
    const { data } = await reportApi.all.postV2ReportCustomShareByLink(body)
    return data
  }

  async shareReportRealTimeByLink(body: ReportDtoT.PostReportRealTimeShareByLinkRequest) {
    const { data } = await reportApi.all.postReportRealTimeShareByLink(body)
    return data
  }

  async downloadV2ReportRealTime(params: ReportDtoT.GetReportRealTimeDownloadV2Request) {
    const { data } = await reportApi.all.getV2ReportRealTimeDownload(params)
    return data
  }

  async fetchShowReportToParents(params: req.ShowReportToParents) {
    await reportApi.all.patchShowReportToParents(params)
  }

  async fetchNavigationRange(params: req.RecentReport) {
    const {
      data: { data },
    } = await reportApi.all.getNavigationRange(params)
    return parse(`${data.year}-${data.month}`, 'yyyy-MM', new Date())
  }

  async fetchReportsById(params: req.ReportsById) {
    const {
      data: { data },
    } = await reportApi.all.getReportsById(params)
    const parsedData = data.map(this.adapter.simpleReport.from)
    const reportMap = CheckableMap.fromArr(parsedData, 'id')
    return new CheckableColl(reportMap)
  }

  async fetchCustomReportsV2ById(params: req.CustomReportsV2ById) {
    const { data } = await reportApi.all.getCustomReportsV2ById(params)
    return data.map(this.adapter.reportV2.from)
  }

  async fetchStudentReport(params: { studentId: Student['id']; reportId: Report['id'] }) {
    const {
      data: { data },
    } = await reportApi.student.getStudentReport(params)
    return new Report(data)
  }

  async fetchMonthlyAvailableReports(studentId: Student['id'], revision: common.Revision) {
    const { data } = await reportApi.student.getMonthlyAvailableReports(studentId, revision)
    return data.data.reverse()
  }

  async download(params: req.DownloadReports) {
    const { data } = await reportApi.getDownload(params)
    return data
  }

  async downloadOneClick(params: req.OneClickReportParams) {
    const res = await reportApi.oneClick.getDownloadOneClick(params)
    return setHeaderType(res)
  }

  async downloadOneClickGroup(params: req.OneClickGroupReportDownloadParams) {
    const { data } = await reportApi.oneClick.getDownloadOneClickGroup(params)
    return data
  }

  async requestCreateOneClickGroupReport(params: req.OneClickGroupReportParams) {
    const { data } = await reportApi.oneClick.postCreateOneClickGroupReport(params)
    return data
  }

  async downloadCasOneClick(params: req.CasOneClickReportParams) {
    const res = await reportApi.oneClick.getDownloadCas(params)
    return setHeaderType(res)
  }

  async downloadCasOneClickGroup(params: req.CasOneClickGroupReportDownloadParams) {
    const { data } = await reportApi.oneClick.getDownloadCasGroup(params)
    return data
  }

  async requestCreateCasOneClickGroupReport(
    params: req.CasOneClickGroupReportParams,
  ) {
    const { data } = await reportApi.oneClick.postCreateCasGroupReport(params)
    return data
  }

  async checkDuplicationReport(params: req.CheckDuplicationReport) {
    const {
      data: { data },
    } = await reportApi.all.getCheckDuplicationReport(params)
    return data
  }

  async createStudentReport(body: req.NewReport) {
    const {
      data: { data },
    } = await reportApi.all.postReport(body, 'student')
    return data
  }

  async createGroupReport(body: req.NewReport) {
    const {
      data: { data },
    } = await reportApi.all.postReport(body, 'group')
    return data
  }

  async updateStudentReport({
    studentId,
    reportId,
    report,
  }: {
    studentId: Student['id']
    reportId: Report['id']
    report: req.CreateUpdateReport
  }) {
    const {
      data: { data },
    } = await reportApi.student.putStudentReport(studentId, reportId, report)
    return data
  }

  async deleteReports(reportIds: ReportModelV2['id'][]) {
    await reportApi.student.deleteReports({ reportIds })
  }

  async checkValidStudentReport(params: req.ReportAvailableStudentIds) {
    const {
      data: { data },
    } = await reportApi.group.getAvailableStudentIds(params)
    if (data.length === 1) {
      return true
    } else {
      throw new CommonError(NOT_AVAILABLE_ERROR)
    }
  }

  async fetchAvailableGroupReportStudentIds(params: req.ReportAvailableStudentIds) {
    const {
      data: { data },
    } = await reportApi.group.getAvailableStudentIds(params)
    if (data.length > 0) {
      return data
    } else {
      throw new CommonError(NOT_AVAILABLE_ERROR)
    }
  }
}

export const reportController = new ReportController()
